import { useState, useCallback, useEffect } from 'react'
import type { JSX } from 'react'

import { IgProductTagIcon, UnstyledButton } from '@buffer-mono/popcorn'

import type {
  Image,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'

import { ArrowLeftIcon, ArrowRightIcon } from '../../../../assets/instagram'
import { getInstagramPreviewHeight } from '../../../Common/utils'
import { VideoPlayer } from '../VideoPlayer'

import styles from './ImageCarousel.module.css'

export type ImageCarouselSlide =
  | { kind: 'image'; image: Image }
  | { kind: 'video'; video: Video }

type ImageCarouselProps = {
  slides: ImageCarouselSlide[]
  stickerProducts?: string
  onIndexChange?: (index: number) => void
  /**
   * The variant determines the sizing behavior of the carousel.
   * - 'post': Height derived from the first slide's aspect ratio (default)
   * - 'story': Full height to fill the story container
   */
  variant?: 'post' | 'story'
}

function dimensionsOf(slide: ImageCarouselSlide): {
  height: number | null
  width: number | null
} {
  return slide.kind === 'image' ? slide.image : slide.video
}

/**
 * Carousel component for navigating through an ordered list of image and
 * video slides. Supports keyboard navigation (Arrow Left/Right) when
 * focused.
 */
export const ImageCarousel = ({
  slides,
  stickerProducts,
  onIndexChange,
  variant = 'post',
}: ImageCarouselProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(true)

  // Compute a safe index that's always within bounds - this prevents crashes
  // during render when slides are removed while viewing a later slide
  const safeIndex = Math.min(currentIndex, Math.max(0, slides.length - 1))

  // Sync the actual state when it differs from the safe index
  useEffect(() => {
    if (currentIndex !== safeIndex) {
      setCurrentIndex(safeIndex)
      onIndexChange?.(safeIndex)
    }
  }, [currentIndex, safeIndex, onIndexChange])

  const currentSlide = slides[safeIndex]

  // Match Instagram's behaviour: the carousel container takes the first
  // slide's aspect ratio; subsequent slides are fit/cropped within it.
  const firstSlide = slides[0]
  const { height, width } = dimensionsOf(firstSlide)
  const previewHeight = getInstagramPreviewHeight({ height, width })

  const hasMultipleSlides = slides.length > 1
  const isFirstSlide = safeIndex === 0
  const isLastSlide = safeIndex === slides.length - 1

  const goToPrevious = useCallback(() => {
    if (!isFirstSlide) {
      const newIndex = safeIndex - 1
      setCurrentIndex(newIndex)
      setImageLoaded(false)
      onIndexChange?.(newIndex)
    }
  }, [safeIndex, isFirstSlide, onIndexChange])

  const goToNext = useCallback(() => {
    if (!isLastSlide) {
      const newIndex = safeIndex + 1
      setCurrentIndex(newIndex)
      setImageLoaded(false)
      onIndexChange?.(newIndex)
    }
  }, [safeIndex, isLastSlide, onIndexChange])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!hasMultipleSlides) return

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    },
    [hasMultipleSlides, goToPrevious, goToNext],
  )

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- The carousel is an intentional composite widget with a labelled region, tab stop, and Arrow key navigation.
    <div
      className={styles.wrapper}
      data-testid="preview-images"
      data-variant={variant}
      style={variant === 'story' ? undefined : { height: `${previewHeight}px` }}
      role={hasMultipleSlides ? 'region' : undefined}
      aria-roledescription={hasMultipleSlides ? 'carousel' : undefined}
      aria-label={
        hasMultipleSlides
          ? `Media carousel, ${safeIndex + 1} of ${
              slides.length
            }. Use arrow keys to navigate.`
          : undefined
      }
      tabIndex={hasMultipleSlides ? 0 : undefined}
      onKeyDown={hasMultipleSlides ? handleKeyDown : undefined}
    >
      {stickerProducts && (
        <div className={styles.productsSticker}>
          <IgProductTagIcon color="inverted" />
        </div>
      )}

      {currentSlide.kind === 'image' ? (
        <img
          key={currentSlide.image.url}
          src={currentSlide.image.url}
          alt={currentSlide.image.altText || `Slide ${safeIndex + 1}`}
          className={`${styles.image} ${imageLoaded ? styles.imageLoaded : ''}`}
          onLoad={(): void => setImageLoaded(true)}
        />
      ) : (
        <VideoPlayer
          key={currentSlide.video.url}
          video={currentSlide.video}
          variant={variant}
        />
      )}

      {hasMultipleSlides && !isFirstSlide && (
        <UnstyledButton
          className={`${styles.navButton} ${styles.navButtonLeft}`}
          onClick={goToPrevious}
          aria-label="Previous slide"
        >
          <ArrowLeftIcon size={30} />
        </UnstyledButton>
      )}

      {hasMultipleSlides && !isLastSlide && (
        <UnstyledButton
          className={`${styles.navButton} ${styles.navButtonRight}`}
          onClick={goToNext}
          aria-label="Next slide"
        >
          <ArrowRightIcon size={30} />
        </UnstyledButton>
      )}
    </div>
  )
}
